import React, { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FormProvider, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Tab,
  Typography,
  IconButton,
  CardMedia,
  Tabs,
  Modal,
  Tooltip,
} from "@mui/material";
import {
  RHFAutocomplete,
  RHFDateTimePicker,
  RHFTextField,
} from "../../RHFComponents/RHFComponents";
import { RHFTimePicker } from "../../RHFComponents/RHFComponents";
import {
  DataGrid,
  GridColDef,
  GridSelectionModel,
  itIT,
  useGridApiRef,
} from "@mui/x-data-grid";
import bluePencil from "../../../assets/icons/Icon-bluePencil.svg";
import redTrashBin from "../../../assets/icons/RedTrashBin.svg";
import copyFromDoctorIcon from "../../../assets/icons/MedintCopyFromDoctorIcon.svg";
import addIcon from "../../../assets/icons/MedintAddIcon.svg";
import { AllertMessage } from "../../DoctorsList/Medint/MedintDetails/MedintDetails";
import {
  DataContext,
  GestioneOrariGiornalieriProps,
} from "../Calendar/Context/ContextWrapper";
import { useCalendarInfo } from "../Calendar/Calendar";
import {
  pamedg,
  pamedgp,
  pamedo,
  pamedp,
  performance,
  specialities,
  typology,
} from "../../../networking";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { formatHour } from "../../AppointmentResearch/AppointmentResearch";
import { Specialty, Typology, Performance } from "../../../services/interfaces";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import SortIcon from "@mui/icons-material/Sort";
import { maxLength } from "zod";

dayjs.extend(customParseFormat);

type FasceOrarie = {
  dalle: string;
  alle: string;
};

// ogni elemento dell’array prestazioni che arriva dalla pamedg
type PrestazioneAPI = {
  CODSPECIA: string;
  CODANAPRE: string;
  CODTPGPRE: string;
  DESANAPRE: string;
  DESSPECIA: string;
  DESTPGPRE: string;
  IDPAMEDGP?: number;
  SWSELEZIONATA?: boolean;
};

// la riga che vuole il DataGrid
type PrestazioneRow = {
  id: string;
  specialita: string;
  prestazione: string;
  tipologia: string;
  _raw: PrestazioneAPI; // forse servirà in seguito
  selected?: boolean;
};

type PrestazioneOrarioAPI = {
  IDPAMEDP: string;
  CODSPECIA: string;
  CODANAPRE: string;
  CODTPGPRE: string;
  NUMMAX: string;
  DURATASTD: string;
  DESSPECIA: string;
  DESANAPRE: string;
  DESTPGPRE: string;
};

type OrarioFissoAPI = {
  IDPAMEDO: string;
  ORARIOFISSO: string;
  NUMMAX: string;
  DURATASTD: string;
  prestazioni: PrestazioneOrarioAPI[];
};

type OrarioFissoRow = {
  id: string;
  orario: string;
  max: number;
  durata: number;
};

type PrestazioneOrarioRow = {
  id: string;
  specialita: string;
  prestazione: string;
  max: number;
  durata: number;
  _orarioId: string;
  _orarioHHmm: string;
  _raw: PrestazioneOrarioAPI;
};

//Ancora non sappiamo bene quali siano i nomi corretti da assegnare ai campi. Nel dubbio, li ho nominati come nel design
type FormValues = {
  tipologia: string | null;
  durataStandard: string | null;
  orarioPrestazione: string;
  numeroMaxPrestazioni: string;
  specialita: string | null;
  prestazione: string | null;
};

type StateFormDettaglioOrariGiornalieri = {
  dataDisponibilita: string;
  medico: string;
  agendaProgramma: string;
  tipologia: string;
  tipologiaDescrizione: string;
  numMaxPrestazioni: string;
  durataStandard: string;
  orariDisponibili: FasceOrarie[];
};

export type ResponseObjectPAMEDG = {
  IDPAMEDG: string;
  IDMEDINT: string;
  CODTPGPRE: string;
  DATARIF: string;
  DESTPGPRE: string;
  CODPGMEDT: string;
  IDPGMEDT: string;
  CODMEDINT: string;
  NOMEMEDICO: string;
  NUMMAX: string;
  DURATASTD: string;
  B1ORADA: string;
  B1ORAA: string;
  B2ORADA: string;
  B2ORAA: string;
  B3ORADA: string;
  B3ORAA: string;
  B4ORADA: string;
  B4ORAA: string;
  B5ORADA: string;
  B5ORAA: string;
  prestazioni: [];
  orariFissi: [];
};

const COLORS = {
  navy: "#003267",
  white: "#FFFFFF",
  zebraEven: "#F8F9FC",
  zebraOdd: "#FFFFFF",
  border: "#CCCCCC",
  text: "#00000099",
};

const sectionTitleSx = { fontWeight: 700, color: COLORS.text, mb: 1 };

export function StyledDataGrid(props: React.ComponentProps<typeof DataGrid>) {
  const apiRef = useGridApiRef();
 
  return (
    <DataGrid
      {...props}
      sx={{
        border: `1px solid ${COLORS.border}`,
        width: "100%",
        borderRadius: 2,
        height: 180,
        display: "flex",
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: COLORS.navy,
          color: COLORS.white,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        },
        "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
        "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
          color: COLORS.white,
        },
        "& .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-sortIcon": {
          color: COLORS.white,
        },
        "& .MuiDataGrid-columnSeparator": { display: "none" },
        "& .MuiDataGrid-row:nth-of-type(even)": {
          backgroundColor: COLORS.zebraEven,
        },
        "& .MuiDataGrid-row:nth-of-type(odd)": {
          backgroundColor: COLORS.zebraOdd,
        },
        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
          outline: "none",
        },
        "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
          {
            outline: "none",
          },
        ...(props.sx || {}),
      }}
      headerHeight={36}
      rowHeight={36}
      hideFooter
      disableColumnFilter
      disableColumnSelector
      disableColumnMenu
      localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
    />
  );
}

const AppointmentManagementDetail = (props: {
  refreshAppointments: () => void;
}) => {
  const context = useContext(DataContext);
  const [tab, setTab] = useState(0);

  //Stati per gestire l'operazione da eseguire attraverso il modale (modifica/creazione)
  const [crud, setCrud] = useState<"" | "C" | "U">("");
  const [targetOrarioId, setTargetOrarioId] = useState<string | null>(null);

  //Stato per gestire le prestazioni abilitate
  const [prestazioniAbilitate, setPrestazioniAbilitate] = useState<
    PrestazioneAPI[]
  >([]);

  //Stati per gestire le prestazioni degli orari fissi
  const [orariFissi, setOrariFissi] = useState<OrarioFissoAPI[]>([]);
  const [selectedOrarioId, setSelectedOrarioId] = useState<string | null>(null);

  // Tengo traccia della riga che sto modificando, così posso pre-compilare i campi del modale
  const [editingOrario, setEditingOrario] = useState<OrarioFissoRow | null>(
    null
  );
  const [editingPrestazione, setEditingPrestazione] =
    useState<PrestazioneOrarioRow | null>(null);
  //Stati per gestire le liste delle specialità e delle prestazioni
  const [specialitiesList, setSpecialitiesList] = useState<Specialty[]>([]);
  const [performancesList, setPerformancesList] = useState<Performance[]>([]);
  const [typologiesList, setTypologiesList] = useState<Typology[]>([]);
  //Stato per gestire lo spinner di caricamento
  const [loadingData, setLoadingData] = useState<boolean>(false);

  // da sistemare
  const defaultValues: FormValues = {
    tipologia: "",
    orarioPrestazione: "",
    numeroMaxPrestazioni: "",
    durataStandard: null,
    specialita: null,
    prestazione: null,
  };

  // id settato se la read della pamedg trova un record associato alla tabella
  // Gestione Orari Giornalieri, utile per definire il tipo di valorizzazioni e il tipo di submit (Create/Update), scopo di boolean
  const [idPamedg, setIdPamedg] = useState<string>("");

  //Per aprire il modale di inserimento degli orari fissi
  const [openOrariFissi, setOpenOrariFissi] = useState(false);
  //Per aprire il modale di inserimento delle prestazioni abilitate
  const [openPrestazioniAbilitate, setOpenPrestazioniAbilitate] =
    useState(false);

  // Allert di conferma/risultato
  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning"
  );
  const [message, setMessage] = useState("");

  //Stati per gestire le prestazioni selezionate/selezionabili nella tabella Prestazioni Abilitate
  const [selPrestazioni, setSelPrestazioni] = useState<GridSelectionModel>([]);
  const [initialSelPrestazioni, setInitialSelPrestazioni] = useState<string[]>(
    []
  );

  //UseEffect per il fetch delle specialità, delle prestazioni e tipologia
  useEffect(() => {
    const init = async () => {
      setLoadingData(true);
      try {
        const specialitaPayload = {
          tipoAgenda: "ALL",
          listino: "",
          medico: [],
          specialita: "",
          prestazione: "",
        };
        const prestazioniPayload = {
          tipoAgenda: "ALL",
          listino: "",
          medico: [],
          specialita: "",
        };
        const tipologiaPayload = {
          listino: "",
          medico: [],
          specialita: "",
          prestazione: "",
        };

        const [specRes, perfRes, typRes] = await Promise.all([
          specialities(specialitaPayload),
          performance(prestazioniPayload),
          typology(tipologiaPayload),
        ]);

        setSpecialitiesList(specRes.data?.dati ?? []);
        setPerformancesList(perfRes.data?.dati ?? []);
        setTypologiesList(typRes.data?.dati ?? []);

        // poi eseguo la fetch dei dati giornalieri
        await requestReadGestioneOrariGiornalieri();
      } catch (err) {
        console.error("Errore init dati:", err);
      } finally {
        setLoadingData(false);
      }
    };

    init();
  }, []);

  //Filtro le specialità e prestazioni per le RHF

  // L'alert si chiude dopo 4 secondi se non è di tipo warning
  useEffect(() => {
    if (!showAllert) return;

    // success / error: chiudi dopo 4s e resetta stato
    if (severity !== "warning") {
      const t = setTimeout(() => {
        setShowAllert(false);
        setSeverity("warning");
        setMessage("");
      }, 4000);
      return () => clearTimeout(t);
    }

    // warning senza messaggio: non mostrare l'alert
    if (!message) {
      setShowAllert(false);
    }
  }, [showAllert, severity, message]);

  const methods = useForm<FormValues>({ defaultValues });

  const methodsFormDettaglioOrariGiornalieri =
    useForm<StateFormDettaglioOrariGiornalieri>({
      defaultValues: {
        dataDisponibilita: "",
        medico: "",
        tipologia: "",
        tipologiaDescrizione: "",
        agendaProgramma: "",
        durataStandard: "",
        numMaxPrestazioni: "",
        orariDisponibili: Array.from({ length: 5 }, () => ({
          dalle: "",
          alle: "",
        })),
      },
    });

  // funzione per il recupero di un eventuale tabella presente sul giorno selezionato in questione
  const requestReadGestioneOrariGiornalieri = async () => {
    try {
      const payload = {
        crud: "R",
        aziendaPrenotazione:
          context?.dataCalendarSelezionato?.aziendaPrenotazione ?? "",
        CODMEDINT: context?.dataCalendarSelezionato?.codiceMedico ?? "",
        CODPGMEDT: context?.dataCalendarSelezionato?.agenda ?? "",
        CODTPGPRE: context?.dataCalendarSelezionato?.tipologia ?? "",
        dataIni: context?.dataCalendarSelezionato?.dataSelezionata ?? "",
        dataFin: context?.dataCalendarSelezionato?.dataSelezionata ?? "",
      };

      const response = await pamedg(payload);

      console.log("response pamedg ", response.data.dati);

      if (response.data.esito === "OK") {
        if (response?.data?.dati?.OrarioGiornaliero[0]?.IDPAMEDG) {
          const raw = response?.data?.dati
            ?.OrarioGiornaliero?.[0] as Partial<ResponseObjectPAMEDG>;

          if (!raw) return;

          const firstMapping: Partial<ResponseObjectPAMEDG> = {
            IDPAMEDG: raw.IDPAMEDG ?? "",
            IDMEDINT: raw.IDMEDINT ?? "",
            CODTPGPRE: raw.CODTPGPRE ?? "",
            DATARIF: raw.DATARIF ?? "",
            DESTPGPRE: raw.DESTPGPRE ?? "",
            CODPGMEDT: raw.CODPGMEDT ?? "",
            IDPGMEDT: raw.IDPGMEDT ?? "",
            CODMEDINT: raw.CODMEDINT ?? "",
            NOMEMEDICO: raw.NOMEMEDICO ?? "",
            NUMMAX: raw.NUMMAX ?? "",
            DURATASTD: raw.DURATASTD ?? "",
            prestazioni: raw.prestazioni ?? [],
            orariFissi: raw.orariFissi ?? [],
          };

          // Costruzione dinamica dei B1ORADA, B1ORAA... B5ORADA, B5ORAA
          const orariDinamici = Array.from({ length: 5 }).reduce(
            (acc: any, _, index) => {
              const i = index + 1;
              const dalleKey = `B${i}ORADA` as keyof ResponseObjectPAMEDG;
              const alleKey = `B${i}ORAA` as keyof ResponseObjectPAMEDG;

              acc[dalleKey] = (raw[dalleKey] as string) ?? "";
              acc[alleKey] = (raw[alleKey] as string) ?? "";

              return acc;
            },
            {} as Partial<ResponseObjectPAMEDG>
          );

          const mapped: ResponseObjectPAMEDG = {
            ...firstMapping,
            ...(orariDinamici as any),
          } as ResponseObjectPAMEDG;

          if (raw?.IDPAMEDG) setIdPamedg(raw?.IDPAMEDG);

          setPrestazioniAbilitate(
            Array.isArray(firstMapping.prestazioni)
              ? (firstMapping.prestazioni as PrestazioneAPI[])
              : []
          );

          setOrariFissi((raw?.orariFissi ?? []) as unknown as OrarioFissoAPI[]);
          // const idPamedgp = firstMapping.prestazioni?.map(
          //   (param: any) => param.IDPAMEDGP
          // );

          // const responsePamedgp = await pamedgp({
          //   crud: "R",
          //   aziendaPrenotazione:
          //     context?.dataCalendarSelezionato?.aziendaPrenotazione,
          //   IDPAMEDGP: idPamedgp,
          // });

          // console.log({ responsePamedgp });

          methodsFormDettaglioOrariGiornalieri.reset({
            dataDisponibilita: mapped.DATARIF,
            medico: mapped.NOMEMEDICO,
            tipologia: mapped.CODTPGPRE,
            tipologiaDescrizione: mapped.DESTPGPRE,
            agendaProgramma: mapped.CODPGMEDT,
            durataStandard: mapped.DURATASTD,
            numMaxPrestazioni: mapped.NUMMAX,
            orariDisponibili: Array.from({ length: 5 }).map((_, i) => {
              const dalleKey = `B${i + 1}ORADA` as keyof ResponseObjectPAMEDG;
              const alleKey = `B${i + 1}ORAA` as keyof ResponseObjectPAMEDG;

              const dalleValue = mapped[dalleKey];
              const alleValue = mapped[alleKey];

              const formattedDalleValue =
                dalleValue !== `0` ? formatHour(dalleValue) : ``;
              const formattedAlleValue =
                alleValue !== `0` ? formatHour(alleValue) : ``;

              return {
                dalle: formattedDalleValue,
                alle: formattedAlleValue,
              };
            }),
          });
        } else {
          methodsFormDettaglioOrariGiornalieri.reset({
            dataDisponibilita:
              context?.dataCalendarSelezionato?.dataSelezionata,
            medico: context?.dataCalendarSelezionato?.medico[0] ?? "",
            tipologia: context?.dataCalendarSelezionato?.tipologia ?? "",
            tipologiaDescrizione:
              context?.dataCalendarSelezionato?.tipologiaDescrizione ?? "",
            agendaProgramma: context?.dataCalendarSelezionato?.agenda ?? "",
            durataStandard: "",
            numMaxPrestazioni: "",
            orariDisponibili: Array.from({ length: 5 }, () => ({
              dalle: "",
              alle: "",
            })),
          });
        }
      }
    } catch (error) {
      console.error("Errore fetch read gestione orari", error);
    }
  };

  // Mappo le prestazioni abilitate nel formato che vuole il DataGrid
  // Per l'id creo una chiave con CODSPECIA, CODANAPRE e CODTPGPRE
  // Aggiungo un index per evitare errori in caso di duplicati
  const mapPrestazioniToRows = (list: PrestazioneAPI[]): PrestazioneRow[] =>
    list.map((p, idx) => ({
      // id: uso codici presenti nella risposta e se qualche campo manca uso un fallback stringa
      id: `${p.CODSPECIA || "NA"}-${p.CODANAPRE || "NA"}-${
        p.CODTPGPRE || "ALL"
      }-${idx}`,

      // Codice breve (CODSPECIA). Se non esiste mostro la descrizione lunga (DESSPECIA)
      specialita: p.CODSPECIA || p.DESSPECIA || "",

      // Passo DESANAPRE (descrizione prestazione)
      prestazione: p.DESANAPRE || "",

      // Passo DESTPGRPE (tipologia)
      tipologia: p.DESTPGPRE || "",

      // Mi porto dietro il raw, potrebbe torna utile in futuro se devo aprire modali/modifiche con tutti i campi originali
      _raw: p,
    }));

  // Ricostruisco le righe solo quando cambia l'array di input
  const prestazioniGridRows = React.useMemo(
    () => mapPrestazioniToRows(prestazioniAbilitate),
    [prestazioniAbilitate]
  );

  //Filtragio delle prestazioni abilitate selezionate
  const prestazioniRowsWithSelected = React.useMemo(() => {
    const selSet = new Set(selPrestazioni as string[]);
    return prestazioniGridRows.map((r) => ({
      ...r,
      selected: selSet.has(r.id),
    }));
  }, [prestazioniGridRows, selPrestazioni]);

  useEffect(() => {
    requestReadGestioneOrariGiornalieri();
  }, []);

  const selectedPrestazione = methods.watch("prestazione");

  // quando cambia la prestazione, aggiorno la specialità collegata
  useEffect(() => {
    if (!selectedPrestazione) return;

    const perf = performancesList.find(
      (p) =>
        ((p as any).CODANAPRE ?? (p as any).PRESTAZIONE) === selectedPrestazione
    );

    const specCode = (perf as any)?.CODSPECIA ?? (perf as any)?.SPECIALITA;

    if (specCode) {
      methods.setValue("specialita", specCode, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    }
  }, [selectedPrestazione, performancesList, methods]);

  useEffect(() => {
    if (!prestazioniGridRows?.length) {
      setSelPrestazioni([]);
      setInitialSelPrestazioni([]);
      return;
    }
    const preselected = prestazioniGridRows
      .filter((r) => r._raw?.SWSELEZIONATA === true)
      .map((r) => r.id);

    setSelPrestazioni(preselected);
    setInitialSelPrestazioni(preselected);
  }, [prestazioniGridRows]);

  const prestazioniCols: GridColDef[] = [
    { field: "specialita", headerName: "Specialità", flex: 0.6 },
    { field: "prestazione", headerName: "Prestazione", flex: 1.4 },
    { field: "tipologia", headerName: "Tipologia", flex: 0.8 },
  ];

  const orderBySelectedCol: GridColDef = {
    field: "__orderBySelected__",
    headerName: "",
    width: 36,
    sortable: true,
    filterable: false,
    disableColumnMenu: true,
    headerAlign: "center",
    align: "center",

    // Restituisco l'icona con il Tooltip
    renderHeader: () => (
      <Tooltip title={"Ordina per selezione"}>
        <SortIcon fontSize="small" />
      </Tooltip>
    ),

    // Non mostro nulla nelle colonne
    renderCell: () => null,
    valueGetter: () => null,

    // ordino per le righe selezionate
    sortComparator: (_v1, _v2, p1, p2) => {
      const sel = new Set(selPrestazioni as string[]);
      const a = sel.has(p1.id as string) ? 1 : 0;
      const b = sel.has(p2.id as string) ? 1 : 0;
      return a - b;
    },
  };

  //Colonna con la parte di filtro per le checkbox delle prestazioni Abilitate
  const prestazioniColsWithSelection: GridColDef[] = useMemo(
    () => [orderBySelectedCol, ...prestazioniCols],
    [prestazioniCols, selPrestazioni]
  );

  // Converto gli orari fissi del payload in righe per la griglia
  // formatto l'ora da HHmm a HH:mm usando formatHour
  // trasformo numMax e duratastd da stringa a number
  const orariFissiRows = React.useMemo<OrarioFissoRow[]>(
    () =>
      (orariFissi ?? []).map((o) => ({
        // id: uso l'ID dell’orario fisso come chiave della riga
        id: o.IDPAMEDO,

        // orario: l'API mi dà "HHmm" (es. "1700"), lo trasformo in "HH:mm" (es. "17:00");
        // se è "0" o vuoto, mostro stringa vuota per evitare rumore in UI
        orario:
          o.ORARIOFISSO && o.ORARIOFISSO !== "0"
            ? formatHour(o.ORARIOFISSO)
            : "",

        // max/durata: arrivano come stringhe, li casto a number oppure a 0
        max: Number(o.NUMMAX || 0),
        durata: Number(o.DURATASTD || 0),
      })),
    [orariFissi]
  );

  // Costruisco la tabella "Prestazioni abilitate nell'orario"
  // per ogni orario fisso, itero sulle sue prestazioni interne e genero una riga.
  // Se è selezionato un orario nella tabella sopra, filtro per quello; altrimenti mostro tutte le prestazioni di tutti gli orari.
  const prestazioniOrarioRows = React.useMemo<PrestazioneOrarioRow[]>(() => {
    // se non ho un orario selezionato, non mostro nulla
    if (!selectedOrarioId) {
      return [];
    }

    const source = orariFissi.filter((o) => o.IDPAMEDO === selectedOrarioId);

    const rows: PrestazioneOrarioRow[] = [];

    for (const o of source) {
      for (const p of o.prestazioni || []) {
        rows.push({
          id: `${o.IDPAMEDO}-${p.CODSPECIA}-${p.CODANAPRE}-${
            p.CODTPGPRE || "ALL"
          }`,
          specialita: p.CODSPECIA || p.DESSPECIA || "",
          prestazione: p.DESANAPRE || "",
          max: Number(p.NUMMAX || o.NUMMAX || 0),
          durata: Number(p.DURATASTD || o.DURATASTD || 0),
          _orarioId: o.IDPAMEDO,
          _orarioHHmm: o.ORARIOFISSO,
          _raw: p,
        });
      }
    }

    return rows;
  }, [orariFissi, selectedOrarioId]);

  const orariFissiCols: GridColDef[] = [
    { field: "orario", headerName: "Orario", flex: 0.6 },
    {
      field: "max",
      headerName: "N° Max",
      flex: 0.5,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "durata",
      headerName: "Durata",
      flex: 0.5,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "actions",
      headerName: "Azioni",
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title={"Modifica"}>
            <IconButton
              size="small"
              onClick={() => openOrarioFisso("U", params.row as OrarioFissoRow)}
            >
              <CardMedia
                sx={{ height: 16, width: 16 }}
                component="img"
                image={bluePencil}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title={"Cancella"}>
            <IconButton
              size="small"
              color="error"
              onClick={() => deleteOrarioFisso(params.row as OrarioFissoRow)}
            >
              <CardMedia
                sx={{ height: 16, width: 16 }}
                component="img"
                image={redTrashBin}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title={"Duplica"}>
            <IconButton size="small">
              <CardMedia
                sx={{ height: 16, width: 16 }}
                component="img"
                image={copyFromDoctorIcon}
              />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const prestazioniOrarioCols: GridColDef[] = [
    { field: "specialita", headerName: "Specialità", flex: 0.6 },
    { field: "prestazione", headerName: "Prestazione", flex: 1.2 },
    {
      field: "max",
      headerName: "N° Max",
      flex: 0.5,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "durata",
      headerName: "Durata",
      flex: 0.6,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "actions",
      headerName: "Azioni",
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title={"Modifica"}>
            <IconButton
              size="small"
              onClick={() =>
                openPrestazioneOrario("U", params.row as PrestazioneOrarioRow)
              }
            >
              <CardMedia
                sx={{ height: 16, width: 16 }}
                component="img"
                image={bluePencil}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title={"Cancella"}>
            <IconButton
              size="small"
              color="error"
              onClick={() =>
                deletePrestazioneOrario(params.row as PrestazioneOrarioRow)
              }
            >
              <CardMedia
                sx={{ height: 16, width: 16 }}
                component="img"
                image={redTrashBin}
              />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const onSubmitFormDettaglioOrariGiornalieri = async (
    data: StateFormDettaglioOrariGiornalieri
  ) => {
    setLoadingData(true);
    try {
      let payload: any = {};

      // funzione per generare dinamicamente i field generati di b_orada, b_oraa
      methodsFormDettaglioOrariGiornalieri
        .getValues("orariDisponibili")
        .forEach((orario, index) => {
          if (orario?.dalle)
            payload[`B${index + 1}ORADA`] = dayjs(orario.dalle, "HH:mm").format(
              "HHmm"
            );
          if (orario?.alle)
            payload[`B${index + 1}ORAA`] = dayjs(orario.alle, "HH:mm").format(
              "HHmm"
            );
        });

      // creo oggetto da inviare in base alla presenza o meno dell'ID
      payload = {
        aziendaPrenotazione:
          context?.dataCalendarSelezionato?.aziendaPrenotazione,
        ...(idPamedg && {
          ...payload,
          crud: "U",
          IDPAMEDG: idPamedg,
        }),
        ...(!idPamedg && {
          ...payload,
          crud: "C",
          CODMEDINT: context?.dataCalendarSelezionato?.codiceMedico,
          CODPGMEDT: context?.dataCalendarSelezionato?.agenda,
          CODTPGPRE: context?.dataCalendarSelezionato?.tipologia,
          DATARIF: context?.dataCalendarSelezionato?.dataSelezionata,
        }),
      };

      const response = await pamedg(payload);

      console.log({ response });

      if (response.data.esito === "OK") {
        setSeverity("success");
        // se ho creato la testata, prendo il nuovo id
        const idGiornata =
          payload.crud === "C" ? response.data?.dati?.IDPAMEDG : idPamedg;

        if (payload.crud === "C" && idGiornata) setIdPamedg(idGiornata);

        //differenzio tra selezione iniziale e attuale
        const initialSet = new Set(initialSelPrestazioni);
        const currentSet = new Set((selPrestazioni as string[]) ?? []);

        const toCreate = prestazioniGridRows.filter(
          (r) => currentSet.has(r.id) && !initialSet.has(r.id)
        );
        const toDelete = prestazioniGridRows.filter(
          (r) => initialSet.has(r.id) && !currentSet.has(r.id)
        );

        try {
          // DELETE delle righe che l'utente ha deselezionato
          for (const r of toDelete) {
            if (r._raw?.IDPAMEDGP) {
              await pamedgp({
                crud: "D",
                aziendaPrenotazione:
                  context?.dataCalendarSelezionato?.aziendaPrenotazione,
                IDPAMEDGP: r._raw.IDPAMEDGP,
              });
            }
          }

          // CREATE delle righe che l'utente ha appena selezionato
          for (const r of toCreate) {
            await pamedgp({
              crud: "C",
              aziendaPrenotazione:
                context?.dataCalendarSelezionato?.aziendaPrenotazione,
              IDPAMEDG: idGiornata,
              CODSPECIA: r._raw.CODSPECIA,
              CODANAPRE: r._raw.CODANAPRE,
              CODTPGPRE: r._raw.CODTPGPRE || "",
            });
          }

          // refresh per recuperare tutte le prestazioni aggiornate
          await requestReadGestioneOrariGiornalieri();

          const msg = `Salvato. Create: ${toCreate.length}, Delete: ${toDelete.length}.`;
          setMessage(response.data.messaggio || msg);
        } catch (e) {
          setSeverity("error");
          setMessage(
            "Errore durante l'allineamento delle prestazioni abilitate."
          );
        }
      } else {
        setSeverity("error");
        setMessage(response.data.messaggio || "Operazione non riuscita.");
      }
      setMessage(response.data.messaggio !== "" ? response.data.messaggio : "");
    } catch (error) {
      console.error("Errore send dati form gestione orari", error);
    } finally {
      setShowAllert(true);
      setLoadingData(false);
    }
  };

  // Salvataggio/aggiornamento di un ORARIO FISSO (modale)
  // Se non ho editingOrario ma ho idPamedg passo il crud a C
  const onSubmitOrarioFisso = async (formValues: FormValues) => {
    setLoadingData(true);
    try {
      const hhmm = formValues.orarioPrestazione
        ? dayjs(formValues.orarioPrestazione, "HH:mm").format("HHmm")
        : "";
      const nmax = formValues.numeroMaxPrestazioni
        ? String(formValues.numeroMaxPrestazioni)
        : "0";
      const durata = formValues.durataStandard
        ? String(formValues.durataStandard)
        : "0";
      let payload: any = {
        aziendaPrenotazione:
          context?.dataCalendarSelezionato?.aziendaPrenotazione,
      };

      if (crud === "U" && editingOrario?.id) {
        payload = {
          ...payload,
          crud: "U",
          IDPAMEDO: editingOrario.id,
          ORARIOFISSO: hhmm,
          NMAX: nmax,
          DURATASTD: durata,
        };
      } else if (crud === "C" && idPamedg) {
        payload = {
          ...payload,
          crud: "C",
          IDPAMEDG: idPamedg,
          ORARIOFISSO: hhmm,
          NMAX: nmax,
          DURATASTD: durata,
        };
      } else {
        setSeverity("error");
        setMessage("Modalità o ID mancanti.");
        setShowAllert(true);
        return;
      }

      const resp = await pamedo(payload);
      if (resp.data?.esito === "OK") {
        setSeverity("success");
        setMessage(resp.data?.messaggio || "Operazione completata.");
        setOpenOrariFissi(false);
        setEditingOrario(null);
        setCrud("");
        await requestReadGestioneOrariGiornalieri();
      } else {
        setSeverity("error");
        setMessage(resp.data?.messaggio || "Salvataggio non riuscito.");
      }
    } catch {
      setSeverity("error");
      setMessage("Errore durante il salvataggio dell'orario fisso.");
    } finally {
      setShowAllert(true);
      setLoadingData(false);
    }
  };

  // ORARI FISSI
  const openOrarioFisso = (mode: "C" | "U", row?: OrarioFissoRow) => {
    setCrud(mode);
    if (mode === "U" && row) {
      methods.reset({
        orarioPrestazione: row.orario || "",
        numeroMaxPrestazioni: row.max ? String(row.max) : "",
        durataStandard: row.durata ? String(row.durata) : "",
        tipologia: null,
        specialita: null,
        prestazione: null,
      });
      setEditingOrario(row);
    } else {
      methods.reset({
        orarioPrestazione: "",
        numeroMaxPrestazioni: "",
        durataStandard: "",
        tipologia: null,
        specialita: null,
        prestazione: null,
      });
      setEditingOrario(null);
    }
    setOpenOrariFissi(true);
  };

  // PRESTAZIONI NELL’ORARIO
  const openPrestazioneOrario = (
    mode: "C" | "U",
    row?: PrestazioneOrarioRow,
    orarioId?: string
  ) => {
    setCrud(mode);
    if (mode === "U" && row) {
      methods.reset({
        specialita: row._raw?.CODSPECIA || null,
        prestazione: row._raw?.CODANAPRE || null,
        tipologia: row._raw?.CODTPGPRE || null,
        numeroMaxPrestazioni: row.max ? String(row.max) : "",
        durataStandard: row.durata ? String(row.durata) : "",
        orarioPrestazione: "",
      });
      setEditingPrestazione(row);
      setTargetOrarioId(row._orarioId || null);
    } else {
      methods.reset({
        specialita: null,
        prestazione: null,
        tipologia: null,
        numeroMaxPrestazioni: "",
        durataStandard: "",
        orarioPrestazione: "",
      });
      setEditingPrestazione(null);
      setTargetOrarioId(orarioId ?? null);
    }
    setOpenPrestazioniAbilitate(true);
  };

  //Cancellazione degli orari sissi
  const deleteOrarioFisso = async (row: OrarioFissoRow) => {
    setLoadingData(true);
    try {
      const resp = await pamedo({
        aziendaPrenotazione:
          context?.dataCalendarSelezionato?.aziendaPrenotazione,
        crud: "D",
        IDPAMEDO: row.id,
      });
      if (resp.data?.esito === "OK") {
        setSeverity("success");
        setMessage(resp.data?.messaggio || "Orario cancellato.");
        await requestReadGestioneOrariGiornalieri();
      } else {
        setSeverity("error");
        setMessage(resp.data?.messaggio || "Cancellazione non riuscita.");
      }
    } catch (e) {
      setSeverity("error");
      setMessage("Errore durante la cancellazione.");
    } finally {
      setShowAllert(true);
      setLoadingData(false);
    }
  };

  // Salvataggio/aggiornamento di una prestazione abilitata nell’orario
  const onSubmitPrestazioneOrario = async (formValues: FormValues) => {
    setLoadingData(true);
    const nmax = formValues.numeroMaxPrestazioni
      ? String(formValues.numeroMaxPrestazioni)
      : "0";
    const durata = formValues.durataStandard
      ? String(formValues.durataStandard)
      : "0";
    const base = {
      aziendaPrenotazione:
        context?.dataCalendarSelezionato?.aziendaPrenotazione,
    };

    try {
      if (crud === "U" && editingPrestazione?._raw?.IDPAMEDP) {
        const del = await pamedp({
          ...base,
          crud: "D",
          IDPAMEDP: editingPrestazione._raw.IDPAMEDP,
        });
        if (del?.data?.esito !== "OK") {
          setSeverity("error");
          setMessage(del?.data?.messaggio || "Cancellazione non riuscita.");
          setShowAllert(true);
          return;
        }

        const idOrario = editingPrestazione._orarioId || selectedOrarioId;

        if (!idOrario) {
          setSeverity("error");
          setMessage("ID orario mancante.");
          setShowAllert(true);
          return;
        }

        const codSpecia =
          formValues.specialita || editingPrestazione._raw.CODSPECIA;
        const codAnapre =
          formValues.prestazione || editingPrestazione._raw.CODANAPRE;
        const codTpgpre =
          formValues.tipologia || editingPrestazione._raw.CODTPGPRE || "T";
        if (!codSpecia || !codAnapre) {
          setSeverity("error");
          setMessage("Compila Specialità e Prestazione.");
          setShowAllert(true);
          return;
        }

        const create = await pamedp({
          ...base,
          crud: "C",
          IDPAMEDO: idOrario,
          CODSPECIA: codSpecia,
          CODANAPRE: codAnapre,
          CODTPGPRE: codTpgpre,
          NMAX: nmax,
          DURATASTD: durata,
        });
        if (create?.data?.esito === "OK") {
          setSeverity("success");
          setMessage(create?.data?.messaggio || "Prestazione aggiornata.");
          setOpenPrestazioniAbilitate(false);
          setEditingPrestazione(null);
          setTargetOrarioId(null);
          setCrud("");
          await requestReadGestioneOrariGiornalieri();
        } else {
          setSeverity("error");
          setMessage(create?.data?.messaggio || "Creazione non riuscita.");
        }
        setShowAllert(true);
        return;
      }

      if (crud === "C") {
        const idOrario = targetOrarioId ?? selectedOrarioId;

        if (!idOrario) {
          setSeverity("error");
          setMessage("Seleziona un orario fisso o usa il + sulla riga.");
          setShowAllert(true);
          return;
        }
        if (!formValues.specialita || !formValues.prestazione) {
          setSeverity("error");
          setMessage("Compila Specialità e Prestazione.");
          setShowAllert(true);
          return;
        }

        const resp = await pamedp({
          ...base,
          crud: "C",
          IDPAMEDO: idOrario,
          CODSPECIA: formValues.specialita,
          CODANAPRE: formValues.prestazione,
          CODTPGPRE: formValues.tipologia || "T",
          NMAX: nmax,
          DURATASTD: durata,
        });
        if (resp?.data?.esito === "OK") {
          setSeverity("success");
          setMessage(resp?.data?.messaggio || "Prestazione inserita.");
          setOpenPrestazioniAbilitate(false);
          setEditingPrestazione(null);
          setTargetOrarioId(null);
          setCrud("");
          await requestReadGestioneOrariGiornalieri();
        } else {
          setSeverity("error");
          setMessage(resp?.data?.messaggio || "Inserimento non riuscito.");
        }
        setShowAllert(true);
        return;
      }
      setSeverity("error");
      setMessage("Modalità non valida.");
    } catch {
      setSeverity("error");
      setMessage("Errore durante il salvataggio della prestazione.");
    } finally {
      setShowAllert(true);
      setLoadingData(false);
    }
  };

  //Cancellazione di una Prestazione Abilitata nell'orario
  const deletePrestazioneOrario = async (row: PrestazioneOrarioRow) => {
    setLoadingData(true);
    try {
      const resp = await pamedp({
        aziendaPrenotazione:
          context?.dataCalendarSelezionato?.aziendaPrenotazione,
        crud: "D",
        IDPAMEDP: row._raw?.IDPAMEDP,
      });
      if (resp.data?.esito === "OK") {
        setSeverity("success");
        setMessage(resp.data?.messaggio || "Prestazione cancellata.");
        await requestReadGestioneOrariGiornalieri();
      } else {
        setSeverity("error");
        setMessage(resp.data?.messaggio || "Cancellazione non riuscita.");
      }
    } catch (e) {
      setSeverity("error");
      setMessage("Errore durante la cancellazione.");
    } finally {
      setShowAllert(true);
      setLoadingData(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Orario Giornaliero - Dettaglio</title>
      </Helmet>
      {/* Per evitare allert vuoti */}
      {showAllert && !!message && (
        <AllertMessage
          showAllert={showAllert}
          setShowAllert={setShowAllert}
          severity={severity}
          message={message}
        />
      )}

      <Modal
        open={openOrariFissi}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          setOpenOrariFissi(false);
          setEditingOrario(null);
          setCrud("");
        }}
        disableEscapeKeyDown
        keepMounted
        aria-labelledby="orari-fissi-title"
        aria-describedby="orari-fissi-desc"
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.2)" } },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(90vw, 720px)",
            maxHeight: "85vh",
            outline: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${COLORS.border}`,
              bgcolor: COLORS.white,
              overflow: "auto",
              maxHeight: "85vh",
            }}
          >
            <Tabs
              value={0}
              onChange={(_, v) => setTab(v)}
              aria-label="Dettaglio"
              sx={{
                minHeight: 38,
                "& .MuiTabs-indicator": {
                  backgroundColor: COLORS.navy,
                  height: 3,
                  borderRadius: 3,
                },
                "& .MuiTab-root": {
                  minHeight: 38,
                  textTransform: "none",
                  color: "#003267 !important",
                  px: 0,
                  mr: 3,
                  fontFamily: "Montserrat",
                  fontWeight: "bold",
                },
                "& .Mui-selected": { color: COLORS.text },
                mb: 1,
              }}
            >
              <Tab id="dettaglioOrariFissi" label="Dettaglio" />
            </Tabs>

            <Typography variant="subtitle2" sx={sectionTitleSx}>
              Orari Fissi nella giornata
            </Typography>
            <Grid
              container
              spacing={3}
              sx={{ mb: 2 }}
              display={"flex"}
              justifyContent={"center"}
            >
              <Grid item xs={12} md={12}>
                <RHFTimePicker
                  control={methods.control}
                  name={"orarioPrestazione"}
                  label="Orario Prestazione"
                  minutesStep={5}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <RHFTextField
                  control={methods.control}
                  name={"numeroMaxPrestazioni"}
                  label="Numero max prestazioni"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 3 }}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <RHFTextField
                  control={methods.control}
                  name={"durataStandard"}
                  label="Durata standard"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 3 }}
                />
              </Grid>
              {/* Footer con Button */}
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlinedError"
                  onClick={() => {
                    setOpenOrariFissi(false);
                  }}
                >
                  Uscita
                </Button>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                display={"flex"}
                justifyContent={"flex-end"}
              >
                <Button
                  variant="contained"
                  onClick={methods.handleSubmit(onSubmitOrarioFisso)}
                  disabled={loadingData}
                >
                  Conferma
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Modal>
      <Modal
        open={openPrestazioniAbilitate}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          setOpenPrestazioniAbilitate(false);
          setEditingPrestazione(null);
          setTargetOrarioId(null);
          setCrud("");
        }}
        disableEscapeKeyDown
        keepMounted
        aria-labelledby="prestazioni-abilitate-title"
        aria-describedby="prestazioni-abilitate-desc"
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.2)" } },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(90vw, 720px)",
            maxHeight: "85vh",
            outline: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${COLORS.border}`,
              bgcolor: COLORS.white,
              overflow: "auto",
              maxHeight: "85vh",
            }}
          >
            <Tabs
              value={0}
              onChange={(_, v) => setTab(v)}
              aria-label="Dettaglio"
              sx={{
                minHeight: 38,
                "& .MuiTabs-indicator": {
                  backgroundColor: COLORS.navy,
                  height: 3,
                  borderRadius: 3,
                },
                "& .MuiTab-root": {
                  minHeight: 38,
                  textTransform: "none",
                  color: "#003267 !important",
                  px: 0,
                  mr: 3,
                  fontFamily: "Montserrat",
                  fontWeight: "bold",
                },
                "& .Mui-selected": { color: COLORS.text },
                mb: 1,
              }}
            >
              <Tab id="dettaglioPrestazioniAbilitate" label="Dettaglio" />
            </Tabs>

            <Typography variant="subtitle2" sx={sectionTitleSx}>
              Prestazioni abilitate nell'orario
            </Typography>
            <Grid
              container
              spacing={3}
              sx={{ mb: 2 }}
              display={"flex"}
              justifyContent={"center"}
            >
              <Grid item xs={12} md={12}>
                <RHFAutocomplete
                  control={methods.control}
                  name="specialita"
                  label="Specialità"
                  size="small"
                  fullWidth
                  options={specialitiesList.map((s) => s.SPECIALITA)}
                  getOptionLabel={(code: string) =>
                    specialitiesList.find((s) => s.SPECIALITA === code)
                      ?.DESCRIZIONE ?? code
                  }
                  isOptionEqualToValue={(o, v) => o === v}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <RHFAutocomplete
                  control={methods.control}
                  name="prestazione"
                  label="Prestazione"
                  size="small"
                  fullWidth
                  options={performancesList.map((p) => (p as any).PRESTAZIONE)}
                  getOptionLabel={(code: string) =>
                    performancesList.find(
                      (p) =>
                        ((p as any).CODANAPRE ?? (p as any).PRESTAZIONE) ===
                        code
                    )?.DESCRIZIONE ?? code
                  }
                  isOptionEqualToValue={(o, v) => o === v}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <RHFAutocomplete
                  control={methods.control}
                  name="tipologia"
                  label="Tipologia"
                  size="small"
                  fullWidth
                  options={typologiesList.map((t) => t.CODICE)}
                  getOptionLabel={(code: string) =>
                    typologiesList.find((t) => t.CODICE === code)
                      ?.DESCRIZIONE ?? code
                  }
                  isOptionEqualToValue={(o, v) => o === v}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  control={methods.control}
                  name={"numeroMaxPrestazioni"}
                  label="Numero max prestazioni"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 3 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  control={methods.control}
                  name={"durataStandard"}
                  label="Durata standard"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 3 }}
                />
              </Grid>
              {/* Footer con Button */}
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlinedError"
                  onClick={() => setOpenPrestazioniAbilitate(false)}
                >
                  Uscita
                </Button>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                display={"flex"}
                justifyContent={"flex-end"}
              >
                <Button
                  variant="contained"
                  onClick={methods.handleSubmit(onSubmitPrestazioneOrario)}
                  disabled={loadingData}
                >
                  Conferma
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Modal>

      <FormProvider {...methodsFormDettaglioOrariGiornalieri}>
        <form
          onSubmit={methodsFormDettaglioOrariGiornalieri.handleSubmit(
            onSubmitFormDettaglioOrariGiornalieri
          )}
        >
          <Box sx={{ p: 2 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${COLORS.border}`,
                mx: "auto",
                bgcolor: COLORS.white,
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  aria-label="Dettaglio"
                  sx={{
                    minHeight: 38,
                    "& .MuiTabs-indicator": {
                      backgroundColor: COLORS.navy,
                      height: 3,
                      borderRadius: 3,
                    },
                    "& .MuiTab-root": {
                      minHeight: 38,
                      textTransform: "none",
                      color: "#003267 !important",
                      px: 0,
                      mr: 3,
                      fontFamily: "Montserrat",
                      fontWeight: "bold",
                    },
                    "& .Mui-selected": { color: COLORS.text },
                  }}
                >
                  <Tab label="Dettaglio" />
                </Tabs>
              </Box>

              <Box>
                <Grid
                  container
                  columnSpacing={2}
                  rowSpacing={2}
                  alignItems="flex-start"
                >
                  {/* Prima riga */}
                  <Grid item xs={12} md={6}>
                    <RHFDateTimePicker
                      name="dataDisponibilita"
                      control={methodsFormDettaglioOrariGiornalieri.control}
                      label="Data disponibilità"
                      dateOnly
                      disabled
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="medico"
                      control={methodsFormDettaglioOrariGiornalieri.control}
                      label="Medico"
                      disabled
                      size="small"
                      fullWidth
                      placeholder="Placeholder"
                    />
                  </Grid>

                  {/* Seconda riga */}
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="agendaProgramma"
                      control={methodsFormDettaglioOrariGiornalieri.control}
                      label="Agenda - Programma"
                      disabled
                      size="small"
                      fullWidth
                      placeholder="Placeholder"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="tipologiaDescrizione"
                      control={methodsFormDettaglioOrariGiornalieri.control}
                      label="Tipologia"
                      disabled
                      size="small"
                      fullWidth
                      placeholder="Placeholder"
                    />
                  </Grid>

                  {/* Terza riga */}
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="numMaxPrestazioni"
                      control={methodsFormDettaglioOrariGiornalieri.control}
                      label="Num. max prestazioni"
                      size="small"
                      fullWidth
                      placeholder="Placeholder"
                      inputProps={{ maxLength: 3 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="durataStandard"
                      control={methodsFormDettaglioOrariGiornalieri.control}
                      label="Durata Standard"
                      size="small"
                      fullWidth
                      placeholder="Placeholder"
                      inputProps={{ maxLength: 3 }}
                    />
                  </Grid>

                  {/* Orari disponibili + Prestazioni abilitate */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={sectionTitleSx}>
                      Orari disponibili
                    </Typography>

                    {methodsFormDettaglioOrariGiornalieri
                      .watch("orariDisponibili")
                      .map((_, i) => (
                        <Grid container spacing={2} key={i} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <RHFTimePicker
                              control={
                                methodsFormDettaglioOrariGiornalieri.control
                              }
                              name={`orariDisponibili.${i}.dalle`}
                              label="Dalle"
                              minutesStep={5}
                              size="small"
                              disabled={
                                i !== 0 &&
                                (!methodsFormDettaglioOrariGiornalieri.watch(
                                  `orariDisponibili.${i - 1}.dalle`
                                ) ||
                                  !methodsFormDettaglioOrariGiornalieri.watch(
                                    `orariDisponibili.${i - 1}.alle`
                                  ))
                              }
                              rules={i === 0 ? { required: true } : undefined}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <RHFTimePicker
                              control={
                                methodsFormDettaglioOrariGiornalieri.control
                              }
                              name={`orariDisponibili.${i}.alle`}
                              label="Alle"
                              minutesStep={5}
                              size="small"
                              disabled={
                                i !== 0 &&
                                (!methodsFormDettaglioOrariGiornalieri.watch(
                                  `orariDisponibili.${i - 1}.dalle`
                                ) ||
                                  !methodsFormDettaglioOrariGiornalieri.watch(
                                    `orariDisponibili.${i - 1}.alle`
                                  ))
                              }
                              rules={i === 0 ? { required: true } : undefined}
                            />
                          </Grid>
                        </Grid>
                      ))}
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Typography variant="subtitle2" sx={sectionTitleSx}>
                      Prestazioni abilitate
                    </Typography>
                    <StyledDataGrid
                      rows={prestazioniGridRows}
                      columns={prestazioniColsWithSelection}
                      checkboxSelection
                      getRowId={(r) => r.id}
                      selectionModel={selPrestazioni}
                      onSelectionModelChange={(m) => setSelPrestazioni(m)}
                      sortingOrder={["desc", "asc", null]}
                      sx={{
                        height: 260,
                        // Nascondo la freccia che esegue il sort, perché tanto lo fa già l'icona
                        '& .MuiDataGrid-columnHeaders [data-field="__orderBySelected__"] .MuiDataGrid-sortIcon':
                          {
                            display: "none",
                          },
                      }}
                    />
                  </Grid>

                  {/* Orari fissi + Prestazioni abilitate nell'orario */}
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={sectionTitleSx}
                        mb={0}
                      >
                        Orari fissi nella giornata
                      </Typography>

                      <Tooltip title="Inserisci orario fisso">
                        <Button
                          variant="outlined"
                          onClick={() => openOrarioFisso("C")}
                          sx={{
                            minWidth: 32,
                            p: 0.5,
                            borderRadius: 1,
                            borderColor: "#003267",
                            bgcolor: "white",
                            "&:hover": {
                              bgcolor: "#003267",
                              borderColor: "#003267",
                            },
                            // gestisco il colore dell'icona via mask
                            "& .addIcon": {
                              WebkitMaskImage: `url(${addIcon})`,
                              maskImage: `url(${addIcon})`,
                              WebkitMaskRepeat: "no-repeat",
                              maskRepeat: "no-repeat",
                              WebkitMaskPosition: "center",
                              maskPosition: "center",
                              WebkitMaskSize: "contain",
                              maskSize: "contain",
                              backgroundColor: "#003267", // default: blu
                              width: 16,
                              height: 16,
                              display: "block",
                            },
                            "&:hover .addIcon": {
                              backgroundColor: "white", // hover: bianco
                            },
                          }}
                        >
                          <Box className="addIcon" />
                        </Button>
                      </Tooltip>
                    </Box>

                    <StyledDataGrid
                      rows={orariFissiRows}
                      columns={orariFissiCols}
                      getRowId={(r) => r.id}
                      sx={{ height: 200 }}
                      selectionModel={
                        selectedOrarioId ? [selectedOrarioId] : []
                      }
                      onSelectionModelChange={(m) => {
                        const id = (m[0] as string) || null;
                        setSelectedOrarioId(id);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={8}>
                    {(() => {
                      const disabled = !selectedOrarioId;
                      return (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={sectionTitleSx}
                              mb={0}
                            >
                              Prestazioni abilitate nell'orario
                            </Typography>

                            <Tooltip
                              title={
                                disabled
                                  ? "Seleziona un orario fisso nella tabella sopra"
                                  : "Inserisci prestazione nell'orario selezionato"
                              }
                            >
                              {/* span perché IconButton disabilitato non attiva il Tooltip */}
                              <span>
                                <Button
                                  variant="outlined"
                                  disabled={disabled}
                                  onClick={() => {
                                    if (!selectedOrarioId) return;
                                    openPrestazioneOrario(
                                      "C",
                                      undefined,
                                      selectedOrarioId
                                    );
                                  }}
                                  sx={{
                                    minWidth: 32,
                                    p: 0.5,
                                    borderRadius: 1,
                                    borderColor: "#003267",
                                    bgcolor: "white",
                                    "&:hover": {
                                      bgcolor: "#003267",
                                      borderColor: "#003267",
                                    },
                                    "& .addIcon": {
                                      WebkitMaskImage: `url(${addIcon})`,
                                      maskImage: `url(${addIcon})`,
                                      WebkitMaskRepeat: "no-repeat",
                                      maskRepeat: "no-repeat",
                                      WebkitMaskPosition: "center",
                                      maskPosition: "center",
                                      WebkitMaskSize: "contain",
                                      maskSize: "contain",
                                      backgroundColor: "#003267",
                                      width: 16,
                                      height: 16,
                                      display: "block",
                                    },
                                    "&:hover .addIcon": {
                                      backgroundColor: "white",
                                    },
                                  }}
                                >
                                  <Box className="addIcon" />
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>

                          <StyledDataGrid
                            rows={prestazioniOrarioRows}
                            columns={prestazioniOrarioCols}
                            getRowId={(r) => r.id}
                            sx={{ height: 200 }}
                          />
                        </>
                      );
                    })()}
                  </Grid>

                  {/* Footer */}
                  <Grid item xs={12} md={6}>
                    <Button
                      variant="outlinedError"
                      color="error"
                      onClick={() => {
                        context?.setShowOrarioGiornaliero(false);
                        props.refreshAppointments();
                      }}
                    >
                      Uscita
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={loadingData}
                      >
                        Conferma
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Box>
        </form>
      </FormProvider>
      {loadingData && (
        <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
          <CircularProgress size="4rem" />
        </Backdrop>
      )}
    </>
  );
};

export default AppointmentManagementDetail;
